// @ts-strict-ignore
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { CustomArticleInfoVO, CustomArticleLibraryVO } from '@project/components/article/model/vo/custom-article.vo';
import { ConfigureCustomArticleMode } from '@project/components/article/custom-article/configure/configure-custom-article-mode';
import { CustomArticleUtils } from '@article/util/custom-article-utils';
import { CustomArticleService } from '@article/service/custom-article.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NumberUtils } from '@shared/util/number-utils';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectComponentPresenter } from '../../../project-component.presenter';

interface FormattedCustomArticleDimensions {
  articleWidth: string;
  articleHeight: string;
  articleDepth: string;
  articleDepthOffset: string;
}

/**
 * Overview of the custom articles
 */
@Component({
  standalone: false,
  templateUrl: './custom-article-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-custom-article-list',
})
export class CustomArticleListComponent implements OnInit {

  @HostBinding(`class.d-flex`) displayFlexClass = true;
  @HostBinding(`class.flex-column`) flexDirectionClass = true;

  @Input()
  library: CustomArticleLibraryVO;
  @Input()
  readonly: boolean;
  @Output()
  public finish = new EventEmitter<void>();


  public update = new EventEmitter<void>();

  selectedArticleInfo: CustomArticleInfoVO;

  customArticleInfoMap: Map<CustomArticleInfoVO, FormattedCustomArticleDimensions> = new Map<CustomArticleInfoVO, FormattedCustomArticleDimensions>();

  constructor(
    private cdRef: ChangeDetectorRef,
    private bsModalRef: BsModalRef,
    private customArticleService: CustomArticleService,
    private localeResourceManager: LocaleResourceManager,
    private projectComponentPresenter: ProjectComponentPresenter,
  ) { }

  public ngOnInit(): void {
    this.updateViewList();
  }

  private updateViewList(): void {
    this.customArticleInfoMap.clear();
    this.library.customArticleInfos.sort((a, b) => StringUtils.stringCompare(a.articleNumber, b.articleNumber))
      .forEach(info => {
        this.customArticleInfoMap.set(info, {
          articleWidth: this.formatDimensionValue(info.articleWidth),
          articleHeight: this.formatDimensionValue(info.articleHeight),
          articleDepth: this.formatDimensionValue(info.articleDepth),
          articleDepthOffset: this.formatDimensionValue(info.articleDepthOffset),
        });
      });
    this.update.emit();
  }

  public editArticleInfo(articleInfo: CustomArticleInfoVO): void {
    this.projectComponentPresenter.openCustomArticleDialog(
      ConfigureCustomArticleMode.CREATE_CUSTOM_ARTICLE,
      this.library,
      articleInfo
    ).then((data) => {
      if (data && data instanceof CustomArticleInfoVO) {
        this.addCustomArticleToLibrary(this.library, data);
        this.updateViewList();
        this.cdRef.markForCheck();
      }
    });
  }

  private addCustomArticleToLibrary(customLibrary: CustomArticleLibraryVO, savedCustomArticle: CustomArticleInfoVO): void {
    let customArticleToSelect: CustomArticleInfoVO =
      CustomArticleUtils.findCustomArticleInfoById(savedCustomArticle.id, customLibrary.customArticleInfos);
    if (customArticleToSelect) {
      this.applyValues(customArticleToSelect, savedCustomArticle);
    } else {
      customLibrary.customArticleInfos.push(savedCustomArticle);
      customArticleToSelect = savedCustomArticle;
    }
    this.selectedArticleInfo = this.library.customArticleInfos.find(item => item === customArticleToSelect);
    this.updateViewList();
  }

  public toggleInfo(selected: CustomArticleInfoVO): void {
    this.selectedArticleInfo = selected;
  }

  public addArticle(): void {
    this.projectComponentPresenter.openCustomArticleDialog(
      ConfigureCustomArticleMode.CREATE_CUSTOM_ARTICLE,
      this.library
    ).then((data) => {
      if (data && data instanceof CustomArticleInfoVO) {
        this.addCustomArticleToLibrary(this.library, data);
        this.updateViewList();
        this.cdRef.markForCheck();
      }
    });
  }

  public deleteArticleInfo(articleInfo: CustomArticleInfoVO): void {
    if (!articleInfo.userId) {
      return;
    }
    this.deleteCustomArticle(articleInfo, articleInfo.userId);
  }

  private deleteCustomArticle(articleInfo: CustomArticleInfoVO, libraryUserId: number): void {
    if (!articleInfo || isNaN(articleInfo.id)) {
      return;
    }
    const articleInfoId: number = articleInfo.id;
    this.customArticleService.deleteCustomArticleById(articleInfoId).subscribe(
      (response) => {
        for (const info of this.library.customArticleInfos) {
          if (articleInfoId === info.id) {
            this.library.customArticleInfos.splice(this.library.customArticleInfos.indexOf(info), 1);
            break;
          }
        }
        this.updateViewList();
        this.cdRef.markForCheck();
      }
    );
  }

  private applyValues(customArticleInfoTo: CustomArticleInfoVO, customArticleInfoFrom: CustomArticleInfoVO): void {
    if (!customArticleInfoTo || !customArticleInfoFrom) {
      return;
    }
    customArticleInfoTo.id = customArticleInfoFrom.id;
    customArticleInfoTo.userId = customArticleInfoFrom.userId;
    customArticleInfoTo.articleNumber = customArticleInfoFrom.articleNumber;
    customArticleInfoTo.articleDescription = customArticleInfoFrom.articleDescription;
    customArticleInfoTo.articleWidth = customArticleInfoFrom.articleWidth;
    customArticleInfoTo.articleHeight = customArticleInfoFrom.articleHeight;
    customArticleInfoTo.articleDepth = customArticleInfoFrom.articleDepth;
    customArticleInfoTo.articleDepthOffset = customArticleInfoFrom.articleDepthOffset;
    customArticleInfoTo.imageHash = customArticleInfoFrom.imageHash;
  }

  public close(): void {
    this.bsModalRef.hide();
    this.finish.emit();
  }

  private formatDimensionValue(value: number, roundValue: boolean = true): string {
    if (NumberUtils.isNotNumber(value)) {
      return null;
    }
    let valueInMillimeters: number = UnitConverter.fromTo('3d', 'mm', value);
    if (roundValue) {
      valueInMillimeters = this.roundNumberValue(valueInMillimeters);
    }
    return this.localeResourceManager.formatNumber(valueInMillimeters, false);
  }

  private roundNumberValue(value: number, precision: number = 2): number {
    return MathUtils.roundNumberValue(value, precision);
  }
}
